import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  memo,
  useEffect,
} from "react";
import PaymeForm, { PaymeFormRef, PaymeFormProps } from "./PaymeForm";
import styles from "./PaymeFormContainer.module.css";

/**
 * A simplified wrapper component for PaymeForm
 *
 * This component has been refactored to provide a more direct connection
 * to the PaymeForm component, reducing unnecessary complexity and timing issues.
 *
 * The memo wrapper prevents unnecessary re-renders.
 */
const PaymeFormContainer = forwardRef<PaymeFormRef, PaymeFormProps>(
  (props, ref) => {
    // Use a stable instance key to prevent remounting
    const [instanceId] = useState(() => Date.now().toString());
    const paymeFormRef = useRef<PaymeFormRef>(null);

    // Only log on mount, not on every render
    useEffect(() => {
      console.log(`PaymeFormContainer (${instanceId}): Mounted`);
      return () => {
        console.log(`PaymeFormContainer (${instanceId}): Unmounted`);
      };
    }, [instanceId]);

    // Forward the PaymeForm methods directly
    useImperativeHandle(ref, () => ({
      processPayment: async () => {
        console.log(
          `PaymeFormContainer (${instanceId}): processPayment called`
        );
        if (paymeFormRef.current) {
          await paymeFormRef.current.processPayment();
        } else {
          console.error("PaymeForm ref is not available");
          props.onError(new Error("Payment form is not initialized"));
        }
      },
      resetForm: () => {
        console.log(`PaymeFormContainer (${instanceId}): resetForm called`);
        if (paymeFormRef.current) {
          paymeFormRef.current.resetForm();
        } else {
          console.error("PaymeForm ref is not available");
          props.onError(
            new Error("Cannot reset payment form - not initialized")
          );
        }
      },
    }));

    return (
      <div className={styles.paymeFormWrapper}>
        <PaymeForm
          key={instanceId} // Use a stable key to prevent remounting
          ref={paymeFormRef}
          customerData={props.customerData}
          shippingData={props.shippingData}
          amount={props.amount}
          onSuccess={props.onSuccess}
          onStartProcessing={props.onStartProcessing}
          onError={props.onError}
        />
      </div>
    );
  }
);

PaymeFormContainer.displayName = "PaymeFormContainer";

// Use React.memo with a custom comparison function to prevent unnecessary re-renders
export default memo(PaymeFormContainer, (prevProps, nextProps) => {
  // Deep comparison of customerData
  const customerDataEqual =
    prevProps.customerData.firstName === nextProps.customerData.firstName &&
    prevProps.customerData.lastName === nextProps.customerData.lastName &&
    prevProps.customerData.email === nextProps.customerData.email &&
    prevProps.customerData.documentType ===
      nextProps.customerData.documentType &&
    prevProps.customerData.documentNumber ===
      nextProps.customerData.documentNumber &&
    prevProps.customerData.phone === nextProps.customerData.phone;

  // Deep comparison of shippingData
  const shippingDataEqual =
    prevProps.shippingData.email === nextProps.shippingData.email &&
    prevProps.shippingData.address === nextProps.shippingData.address &&
    prevProps.shippingData.district === nextProps.shippingData.district &&
    prevProps.shippingData.province === nextProps.shippingData.province &&
    prevProps.shippingData.department === nextProps.shippingData.department &&
    prevProps.shippingData.country === nextProps.shippingData.country &&
    prevProps.shippingData.ubigeoCode === nextProps.shippingData.ubigeoCode;

  // Only re-render if amount changes or customer/shipping data changes
  return (
    prevProps.amount === nextProps.amount &&
    customerDataEqual &&
    shippingDataEqual &&
    prevProps.onSuccess === nextProps.onSuccess &&
    prevProps.onError === nextProps.onError &&
    prevProps.onStartProcessing === nextProps.onStartProcessing
  );
});
